/**
 * The work board — Figma `04/ Work/ Board`, frame **12:416**, board container
 * **12:554**, the five columns **12:997 / 1061 / 1223 / 1269 / 1333**.
 *
 * ===========================================================================
 * THE COLUMNS ARE THE SERVER'S, AND THE ORDER IS THE SHARED MAP'S
 * ===========================================================================
 * `GET /countries/:cc/work-board` returns all five columns every time, already
 * grouped by `columnForWorkOrderStatus` from `@crf/shared` — the SAME function
 * the API groups with and the Phase 5 dashboards will read. This screen renders
 * what arrived and computes no membership of its own. That is the ESC-2 ruling
 * made structural: a local mapping here is exactly how a column silently
 * under-counts, and there is not one.
 *
 * Terminal work (`deployed`, `closed`) is absent by ruling, not by bug — the
 * shared map returns null for it and the API drops it. The board is what is
 * OUTSTANDING.
 *
 * ===========================================================================
 * WHAT THIS SCREEN DELIBERATELY DOES NOT DO YET
 * ===========================================================================
 * DRAG. The inquiries board drags because `PATCH /inquiries/:id/stage` accepts a
 * target stage. A work order has no equivalent: every move is the CONSEQUENCE of
 * recording a fact (an assignment, a test round, a deployment), and the DTOs
 * carry no `status` field precisely so a card cannot be dragged into a state
 * nobody earned. Offering a drag that then had to be refused would teach the
 * operator the board lies. Moves happen on the record surfaces.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
// TYPE-ONLY, and that is the house rule (`features/inquiries/api.ts:36`):
// `@crf/shared` compiles to CommonJS, so a VALUE import from it cannot be
// statically resolved by rollup and breaks the production bundle. This screen
// used to import WORK_BOARD_COLUMNS as a value; the web build caught it.
// It needs no constant anyway — see below.
import type { WorkBoardColumn } from '@crf/shared';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { Alert, CountryBadge } from '../../components/ui';
import { WorkTypeBadge } from '../crfs/WorkTypeBadge';
import { useWorkBoard } from './use-work';
import type { WorkOrderDto } from './api';

/**
 * ⚠ THIS MAP IS `work_orders.type` — WHAT KIND OF JOB — AND IT IS NOT THE PROJECTS
 * `work_type`, WHICH IS WHAT KIND OF RECORD. Two different columns, and after
 * migration 37 the two words collide on this one screen: a card can be a
 * `ready_deploy` job on a `project` record. The badge beside the label carries the
 * second one and comes from `features/crfs/domain.ts`'s `WORK_TYPE_LABEL`. The
 * local constant below keeps its name because it is `WorkOrderType`'s, and renaming
 * a shipped identifier to resolve a reader's ambiguity is the change design D-8
 * refuses; the ambiguity is resolved here, in words.
 */
const WORK_ORDER_TYPE_LABEL: Readonly<Record<string, string>> = {
  new: 'New',
  correction: 'Correction',
  dl_file: 'DL file',
  ready_deploy: 'Ready to deploy',
};

export function WorkBoardPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  // `crf.view` is the API's gate on this route — read the board, not write it.
  const { can } = useSession();
  const canView = can(countryCode, 'crf.view');
  const query = useWorkBoard(countryCode, canView);

  const total = useMemo(
    () => (query.data?.columns ?? []).reduce((sum, column) => sum + column.count, 0),
    [query.data],
  );

  if (!canView) {
    return (
      <div className="p-6">
        <Alert tone="info" title="The work board is not part of your access">
          Seeing development work needs the <code>crf.view</code> permission. Your other
          screens are unaffected.
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-h2 font-semibold text-fg">
            Work board
            <CountryBadge code={countryCode} />
          </h1>
          <p className="text-body13 text-fg-muted">
            {/* The honest count: what the SERVER said, not what is on screen —
              * the same rule the sales-order list learned. */}
            {query.isPending
              ? 'Loading the board…'
              : query.isError
                ? 'The board could not be read.'
                : `${total} outstanding ${total === 1 ? 'work order' : 'work orders'}. Deployed and closed work leaves the board.`}
          </p>
        </div>
      </header>

      {query.isError ? (
        <Alert tone="danger" title="The board could not be read">
          This is the request failing, not an empty board — the difference matters, so
          nothing below is shown rather than showing zero columns as if they were counted.
        </Alert>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-x-auto md:grid-cols-5">
          {/* THE SERVER'S COLUMNS, IN THE SERVER'S ORDER. The response always
            * carries all five, grouped by the published @crf/shared map, so this
            * screen needs no column constant of its own — which is both the ESC-2
            * rule (one table, not a copy) and the reason the type-only import
            * above is sufficient. While loading there is no response yet, so the
            * skeleton is a fixed count of placeholder columns that assert
            * nothing about membership. */}
          {query.isPending
            ? Array.from({ length: 5 }, (_, index) => (
                <BoardColumnSkeleton key={index} />
              ))
            : (query.data?.columns ?? []).map((column) => (
                <BoardColumn
                  key={column.column}
                  column={column.column}
                  label={column.label}
                  cards={column.cards}
                  count={column.count}
                  countryCode={countryCode}
                />
              ))}
        </div>
      )}
    </div>
  );
}

/** A placeholder column: it names nothing, because nothing has been counted. */
function BoardColumnSkeleton(): React.JSX.Element {
  return (
    <section
      aria-hidden="true"
      className="flex min-h-0 flex-col rounded-md border border-line bg-subtle/40"
    >
      <div className="h-9 border-b border-line" />
      <div className="flex flex-1 flex-col gap-2 p-2">
        <div className="h-16 animate-pulse rounded-sm bg-surface" />
      </div>
    </section>
  );
}

function BoardColumn({
  column,
  label,
  cards,
  count,
  countryCode,
}: {
  column: WorkBoardColumn;
  label: string;
  cards: readonly WorkOrderDto[];
  count: number;
  countryCode: string;
}): React.JSX.Element {
  return (
    <section
      aria-label={label}
      data-column={column}
      className="flex min-h-0 flex-col rounded-md border border-line bg-subtle/40"
    >
      <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <h2 className="text-body13 font-medium text-fg">{label}</h2>
        <span className="tnum rounded-pill bg-surface px-2 py-0.5 text-label text-fg-muted">
          {count}
        </span>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
        {cards.length === 0 ? (
          // An empty column is a real, common state and says so in words rather
          // than leaving a blank the operator has to interpret.
          <p className="px-1 py-2 text-label text-fg-muted">Nothing here.</p>
        ) : (
          cards.map((card) => (
            <Link
              key={card.id}
              to={`/countries/${countryCode}/work/${card.id}`}
              className="block rounded-sm border border-line bg-surface p-2.5 hover:border-line-strong"
            >
              <p className="truncate text-body13 font-medium text-fg">
                {card.crf_doc_no ?? `Work order ${card.id}`}
              </p>
              {/*
                THE BADGE SITS BESIDE THE EXISTING LABEL, not in place of it: the
                label says what job this is, the badge says what kind of record it
                hangs off, and both are true at once. It draws only for projects —
                see `WorkTypeBadge`'s header on why marking the majority is noise.

                The card ALREADY distinguished the two for a reader who parses
                document numbers (`IN-PROJ/…` against `IN-CRF/…`), and that is
                exactly why the badge is not redundant: a distinction that depends
                on a human reading a string is not a distinction a screenshot, a
                colour-blind reader or a CSV can act on.
              */}
              <p className="mt-0.5 flex items-center gap-1.5 text-label text-fg-muted">
                <WorkTypeBadge workType={card.work_type} />
                <span>
                  {WORK_ORDER_TYPE_LABEL[card.type] ?? card.type}
                  {card.assigned_to === null ? ' · Unassigned' : ''}
                </span>
              </p>
            </Link>
          ))
        )}
      </div>
    </section>
  );
}
